import React, { useState, useMemo, useEffect } from 'react';

/*
=============================================================================
AUDIO TRADER - SHIPPING DASHBOARD
Professional Design System
=============================================================================
*/

// API Base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// =============================================================================
// API FUNCTIONS
// =============================================================================

const api = {
  async getDeals() {
    const res = await fetch(`${API_URL}/api/hubspot/deals`);
    if (!res.ok) throw new Error('Failed to fetch deals');
    return res.json();
  },
  
  async updateDealStage(dealId, stage, data = {}) {
    const res = await fetch(`${API_URL}/api/hubspot/update-stage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dealId, stage, ...data })
    });
    return res.json();
  },

  async getRates(shipmentData) {
    const res = await fetch(`${API_URL}/api/fedex/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(shipmentData)
    });
    if (!res.ok) throw new Error('Failed to get rates');
    return res.json();
  },
  
  async createShipment(shipmentData) {
    const res = await fetch(`${API_URL}/api/fedex/ship`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(shipmentData)
    });
    return res.json();
  },
  
  async schedulePickup(pickupData) {
    const res = await fetch(`${API_URL}/api/fedex/pickup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pickupData)
    });
    return res.json();
  },

  async sendConfirmation(emailData) {
    const res = await fetch(`${API_URL}/api/postmark/shipping-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData)
    });
    return res.json();
  },

  async sendEmail(emailData) {
    const res = await fetch(`${API_URL}/api/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData)
    });
    return res.json();
  },

  async uploadLabel(file, dealId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dealId', dealId);
    const res = await fetch(`${API_URL}/api/r2/upload-label`, {
      method: 'POST',
      body: formData
    });
    return res.json();
  },

  async uploadMultipleLabels(files, dealId) {
    const formData = new FormData();
    formData.append('dealId', dealId);
    files.forEach(file => formData.append('files', file));
    const res = await fetch(`${API_URL}/api/r2/upload-labels`, {
      method: 'POST',
      body: formData
    });
    return res.json();
  }
};

// =============================================================================
// DESIGN SYSTEM
// =============================================================================

const theme = {
  colors: {
    // Primary
    black: '#222725',
    white: '#FFFFFF',
    cream: '#F2EDE4',
    offWhite: '#F8F9F8',
    
    // Accents
    green: '#3BB273',
    greenLight: 'rgba(59, 178, 115, 0.1)',
    greenMid: 'rgba(59, 178, 115, 0.2)',
    
    yellow: '#FFD44D',
    yellowLight: 'rgba(255, 212, 77, 0.15)',
    
    blue: '#4E8098',
    blueLight: 'rgba(78, 128, 152, 0.1)',
    
    red: '#E5625E',
    redLight: 'rgba(229, 98, 94, 0.1)',
    
    // Neutrals
    grey100: '#F5F5F5',
    grey200: '#E8E8E8',
    grey300: '#D1D1D1',
    grey400: '#A0A0A0',
    grey500: '#6B6B6B',
    grey600: '#4A4A4A',
  },
  
  shadows: {
    sm: '0 1px 2px rgba(34, 39, 37, 0.05)',
    md: '0 2px 8px rgba(34, 39, 37, 0.08)',
    lg: '0 4px 16px rgba(34, 39, 37, 0.12)',
    xl: '0 8px 32px rgba(34, 39, 37, 0.16)',
  },
  
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  }
};

// Global styles
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: ${theme.colors.cream};
    color: ${theme.colors.black};
    -webkit-font-smoothing: antialiased;
  }
  
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.grey300};
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.grey400};
  }
`;

// =============================================================================
// UTILITIES
// =============================================================================

const isNorthernIreland = (postcode) => {
  if (!postcode) return false;
  const prefix = postcode.toUpperCase().replace(/\s/g, '').substring(0, 2);
  return prefix === 'BT';
};

const formatWeight = (weight) => `${weight}kg`;

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short' 
  });
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long',
    year: 'numeric'
  });
};

// =============================================================================
// CARRIER LOGIC
// =============================================================================

const CARRIERS = {
  royal_mail: {
    id: 'royal_mail',
    name: 'Royal Mail',
    color: theme.colors.red,
    maxWeight: 2,
    maxBoxes: 5,
    conditions: 'Single box ≤2kg'
  },
  parcelforce: {
    id: 'parcelforce',
    name: 'ParcelForce',
    color: theme.colors.blue,
    maxWeight: 30,
    conditions: 'Up to 30kg per box'
  },
  fedex: {
    id: 'fedex',
    name: 'FedEx',
    color: '#4D148C',
    conditions: 'Heavy/multiple boxes'
  },
  shiply: {
    id: 'shiply',
    name: 'Shiply',
    color: theme.colors.yellow,
    conditions: 'Large/bulky items'
  }
};

// Check if a single box fits Royal Mail dimensions (max 61 x 46 x 46 cm)
function fitsRoyalMailDimensions(box) {
  if (!box) return false;
  const dims = [box.length || 0, box.width || 0, box.depth || 0].sort((a, b) => b - a);
  return dims[0] <= 61 && dims[1] <= 46 && dims[2] <= 46;
}

function getLongestSide(boxes) {
  if (!boxes || boxes.length === 0) return 0;
  let longest = 0;
  for (const box of boxes) {
    const maxSide = Math.max(box.length || 0, box.width || 0, box.depth || 0);
    if (maxSide > longest) longest = maxSide;
  }
  return longest;
}

function allBoxesFitRoyalMailDimensions(boxes) {
  if (!boxes || boxes.length === 0) return false;
  return boxes.every(box => fitsRoyalMailDimensions(box));
}

// Check if fits ParcelForce dimensions (max 150cm longest side, L+2(W+H) < 300cm)
function fitsParcelForceDimensions(box) {
  if (!box) return false;
  const l = box.length || 0;
  const w = box.width || 0;
  const h = box.depth || 0;
  const dims = [l, w, h].sort((a, b) => b - a);
  const longest = dims[0];
  const girth = 2 * (dims[1] + dims[2]);
  const combined = longest + girth;
  
  return longest <= 150 && combined <= 300;
}

function allBoxesFitParcelForceDimensions(boxes) {
  if (!boxes || boxes.length === 0) return false;
  return boxes.every(box => fitsParcelForceDimensions(box));
}


function assignCarrier(shipment) {
  const {
    postcode,
    boxes = [],
    totalWeight = 0,
    hasPrinter = false,
    sendType = 'courier'
  } = shipment;

  const calculatedTotalWeight = boxes.length > 0
    ? boxes.reduce((sum, b) => sum + (b.weight || 0), 0)
    : totalWeight;

  const boxCount = boxes.length || 1;
  const isSingleBox = boxCount === 1;
  const longestSide = getLongestSide(boxes);
  const isNI = isNorthernIreland(postcode);
  const isDropOff = sendType === 'dropoff';
  const rmWeightLimit = isNI ? 20 : 10;
  const fitsRMWeight = calculatedTotalWeight <= rmWeightLimit;
  const fitsRMDimensions = allBoxesFitRoyalMailDimensions(boxes);
  const fitsRM = fitsRMWeight && fitsRMDimensions;
  const fitsPF = allBoxesFitParcelForceDimensions(boxes);
  const isOversized = longestSide > 61;

  // STEP 1: SHIPLY for extreme cases (very heavy or very large)
  if (calculatedTotalWeight >= 70 || longestSide > 150) {
    return { carrier: CARRIERS.shiply, carrierId: 'shiply', reason: 'Exceeds standard limits (>70kg or >150cm)' };
  }

  // STEP 2: DROP-OFF FLOW
  if (isDropOff) {
    // Royal Mail: up to 10kg (20kg NI), max 61x46x46cm
    if (fitsRM) {
      return { carrier: CARRIERS.royal_mail, carrierId: 'royal_mail', reason: 'Drop-off within RM limits' };
    }
    
    // ParcelForce: up to 30kg, max 150cm longest side, fits PF dimensions
    if (calculatedTotalWeight <= 30 && fitsPF) {
      return { carrier: CARRIERS.parcelforce, carrierId: 'parcelforce', reason: 'Drop-off within ParcelForce limits' };
    }
    
    // Too big/heavy for drop-off services
    return { carrier: CARRIERS.shiply, carrierId: 'shiply', notAllowed: true, error: 'Exceeds drop-off limits (>30kg or >150cm)' };
  }

  // STEP 3: NORTHERN IRELAND
  if (isNI) {
    if (isSingleBox && fitsRM) {
      return { carrier: CARRIERS.royal_mail, carrierId: 'royal_mail', reason: 'NI within RM limits' };
    }
    if (calculatedTotalWeight <= 30 && !hasPrinter && fitsPF) {
      return { carrier: CARRIERS.parcelforce, carrierId: 'parcelforce', reason: 'NI no printer ≤30kg' };
    }
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'NI with printer or heavy' };
  }

  // STEP 4a: OVERSIZED CHECK (>61cm longest side but within PF limits)
  if (isOversized) {
    if (hasPrinter) {
      return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'Oversized with printer' };
    }
    if (calculatedTotalWeight <= 30 && fitsPF) {
      return { carrier: CARRIERS.parcelforce, carrierId: 'parcelforce', reason: 'Oversized no printer ≤30kg, within PF limits' };
    }
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'Oversized heavy', postLabel: true };
  }

  // STEP 4b: MULTI-BOX
  if (!isSingleBox) {
    if (hasPrinter) {
      return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'Multi-box with printer' };
    }
    if (calculatedTotalWeight <= 30) {
      return { carrier: CARRIERS.parcelforce, carrierId: 'parcelforce', reason: 'Multi-box no printer ≤30kg' };
    }
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'Multi-box heavy', postLabel: true };
  }

  // STEP 4c: SINGLE BOX within RM dimensions
  if (fitsRM) {
    return { carrier: CARRIERS.royal_mail, carrierId: 'royal_mail', reason: 'Within RM limits' };
  }
  if (!hasPrinter && calculatedTotalWeight <= 20 && fitsRMDimensions) {
    return { carrier: CARRIERS.royal_mail, carrierId: 'royal_mail', reason: '10-20kg QR code' };
  }
  if (hasPrinter && calculatedTotalWeight <= 20) {
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: '10-20kg with printer' };
  }
  if (!hasPrinter && calculatedTotalWeight <= 30) {
    return { carrier: CARRIERS.parcelforce, carrierId: 'parcelforce', reason: '20-30kg no printer' };
  }
  if (hasPrinter && calculatedTotalWeight <= 30) {
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: '20-30kg with printer' };
  }
  if (calculatedTotalWeight > 30) {
    return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: '>30kg', postLabel: !hasPrinter };
  }

  return { carrier: CARRIERS.fedex, carrierId: 'fedex', reason: 'Fallback' };
}

// FedEx rates
const mockRates = [
  {
    serviceType: 'FEDEX_UK_PRIORITY',
    serviceName: 'FedEx UK Priority',
    transitDays: 1,
    deliveryDate: '2026-01-09',
    price: { currency: 'GBP', totalNet: 18.99 }
  },
  {
    serviceType: 'FEDEX_UK_ECONOMY',
    serviceName: 'FedEx UK Economy',
    transitDays: 3,
    deliveryDate: '2026-01-11',
    price: { currency: 'GBP', totalNet: 12.50 }
  }
];

const ALLOWED_SERVICES = [
  'FEDEX_PRIORITY', 'FEDEX_UK_PRIORITY', 'UK_PRIORITY', 'PRIORITY_OVERNIGHT',
  'FEDEX_ECONOMY', 'FEDEX_UK_ECONOMY', 'UK_ECONOMY', 'FEDEX_GROUND',
  'GROUND_HOME_DELIVERY', 'FEDEX_EXPRESS_SAVER', 'STANDARD_OVERNIGHT'
];

function filterAndSelectCheapest(rates) {
  const filtered = rates.filter(rate => 
    ALLOWED_SERVICES.some(svc => 
      rate.serviceType?.toUpperCase().includes(svc) || 
      rate.serviceType?.toUpperCase() === svc
    )
  );
  if (filtered.length === 0) return { rates: [], cheapest: null };
  filtered.sort((a, b) => a.price.totalNet - b.price.totalNet);
  return { rates: filtered, cheapest: filtered[0] };
}

// =============================================================================
// COMPONENTS
// =============================================================================

// Copy Button
const CopyButton = ({ text, size = 'sm' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const sizes = {
    sm: { padding: '4px 8px', fontSize: '11px' },
    md: { padding: '6px 12px', fontSize: '12px' }
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        background: copied ? theme.colors.green : theme.colors.black,
        color: theme.colors.white,
        border: 'none',
        borderRadius: theme.radius.sm,
        ...sizes[size],
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: 500,
        transition: 'all 0.15s ease',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
};

// Info Row
const InfoRow = ({ label, value, mono = false, copyable = false }) => {
  if (!value) return null;
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: `1px solid ${theme.colors.grey200}`
    }}>
      <span style={{ 
        fontSize: '13px', 
        color: theme.colors.grey500,
        fontWeight: 500
      }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          fontSize: '14px', 
          color: theme.colors.black,
          fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
          fontWeight: mono ? 500 : 600
        }}>
          {value}
        </span>
        {copyable && <CopyButton text={value} />}
      </div>
    </div>
  );
};

// Badge
const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const variants = {
    default: { bg: theme.colors.grey200, color: theme.colors.grey600 },
    success: { bg: theme.colors.greenLight, color: theme.colors.green },
    warning: { bg: theme.colors.yellowLight, color: '#B8860B' },
    error: { bg: theme.colors.redLight, color: theme.colors.red },
    info: { bg: theme.colors.blueLight, color: theme.colors.blue },
    dark: { bg: theme.colors.black, color: theme.colors.white }
  };
  
  const sizes = {
    sm: { padding: '4px 10px', fontSize: '11px' },
    md: { padding: '6px 14px', fontSize: '12px' }
  };
  
  const style = variants[variant];
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: style.bg,
      color: style.color,
      ...sizes[size],
      borderRadius: '100px',
      fontWeight: 600,
      letterSpacing: '0.01em',
      whiteSpace: 'nowrap'
    }}>
      {children}
    </span>
  );
};

// Card
const Card = ({ children, selected = false, onClick, style = {} }) => (
  <div
    onClick={onClick}
    style={{
      background: theme.colors.white,
      borderRadius: theme.radius.lg,
      boxShadow: selected ? `0 0 0 2px ${theme.colors.green}` : theme.shadows.sm,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s ease',
      overflow: 'hidden',
      ...style
    }}
  >
    {children}
  </div>
);

// Shipment Card
const ShipmentCard = ({ shipment, onClick, isSelected }) => {
  const { carrier, reason } = assignCarrier(shipment);
  const hasPreferredDate = !!shipment.preferredPickupDate;
  const totalWeight = shipment.boxes?.reduce((sum, b) => sum + (b.weight || 0), 0) || 0;
  const isNI = isNorthernIreland(shipment.postcode);
  const isDropOff = shipment.sendType === 'dropoff';
  const hasPrinter = shipment.hasPrinter;
  
  return (
    <Card 
      selected={isSelected} 
      onClick={onClick}
      style={{ marginBottom: '12px' }}
    >
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '12px'
        }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '12px',
            fontWeight: 500,
            color: theme.colors.black,
            background: theme.colors.cream,
            padding: '4px 8px',
            borderRadius: theme.radius.sm
          }}>
            {shipment.dealId}
          </span>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {isNI && <Badge variant="warning" size="sm">NI</Badge>}
            {isDropOff && <Badge variant="info" size="sm">Drop-off</Badge>}
            {hasPrinter && <Badge variant="default" size="sm">🖨️</Badge>}
          </div>
        </div>
        
        {/* Customer & Location */}
        <h3 style={{ 
          fontSize: '14px', 
          fontWeight: 600, 
          color: theme.colors.black,
          marginBottom: '4px',
          lineHeight: 1.3
        }}>
          {shipment.customerName}
        </h3>
        <p style={{ 
          fontSize: '13px', 
          color: theme.colors.grey500,
          marginBottom: '12px'
        }}>
          {shipment.postcode}
        </p>
        
        {/* Package Info */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          <Badge variant="default">
            {shipment.boxes?.length || 1} box{shipment.boxes?.length !== 1 ? 'es' : ''}
          </Badge>
          <Badge variant="default">{totalWeight}kg</Badge>
        </div>
        
        {/* Date Status */}
        {hasPreferredDate ? (
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: theme.colors.green,
            fontWeight: 500
          }}>
            <span>📅</span>
            {formatDate(shipment.preferredPickupDate)}
          </div>
        ) : (
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: '#B8860B',
            fontWeight: 500
          }}>
            <span>⚠</span>
            No date set
          </div>
        )}
      </div>
    </Card>
  );
};

// Booked Shipment Card
const BookedShipmentCard = ({ shipment, onClick, isSelected }) => {
  const carrierInfo = CARRIERS[shipment.carrier?.toLowerCase()] || CARRIERS.fedex;
  
  return (
    <Card 
      selected={isSelected} 
      onClick={onClick}
      style={{ marginBottom: '12px' }}
    >
      <div style={{ 
        padding: '4px 16px', 
        background: theme.colors.greenLight,
        borderBottom: `1px solid ${theme.colors.greenMid}`
      }}>
        <span style={{ 
          fontSize: '11px', 
          fontWeight: 600, 
          color: theme.colors.green,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          ✓ Booked via {shipment.carrier || 'Carrier'}
        </span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '12px'
        }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '12px',
            fontWeight: 500,
            color: theme.colors.black,
            background: theme.colors.cream,
            padding: '4px 8px',
            borderRadius: theme.radius.sm
          }}>
            {shipment.dealId}
          </span>
        </div>
        
        <h3 style={{ 
          fontSize: '14px', 
          fontWeight: 600, 
          color: theme.colors.black,
          marginBottom: '4px'
        }}>
          {shipment.customerName}
        </h3>
        <p style={{ 
          fontSize: '13px', 
          color: theme.colors.grey500,
          marginBottom: '12px'
        }}>
          {shipment.postcode}
        </p>
        
        {shipment.trackingNumber && (
          <div style={{
            background: theme.colors.cream,
            padding: '8px 12px',
            borderRadius: theme.radius.md,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '12px',
            color: theme.colors.black,
            fontWeight: 500
          }}>
            {shipment.trackingNumber}
          </div>
        )}
      </div>
    </Card>
  );
};

// Column
const Column = ({ title, count, color, icon, children, variant = 'default' }) => {
  const isBooked = variant === 'booked';
  
  return (
    <div style={{
      flex: '0 0 320px',
      width: '320px',
      background: theme.colors.white,
      borderRadius: theme.radius.xl,
      boxShadow: theme.shadows.md,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 140px)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: `1px solid ${theme.colors.grey200}`
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {icon && (
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: theme.radius.md,
                background: isBooked ? theme.colors.greenLight : `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px'
              }}>
                {icon}
              </div>
            )}
            <h2 style={{ 
              fontSize: '16px', 
              fontWeight: 700,
              color: theme.colors.black
            }}>
              {title}
            </h2>
          </div>
          <Badge variant={isBooked ? 'success' : 'dark'} size="md">
            {count}
          </Badge>
        </div>
      </div>
      
      {/* Content */}
      <div style={{ 
        padding: '16px', 
        flex: 1, 
        overflowY: 'auto',
        background: theme.colors.offWhite
      }}>
        {children}
      </div>
    </div>
  );
};

// Empty State
const EmptyState = ({ message }) => (
  <div style={{
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.colors.grey400
  }}>
    <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>📦</div>
    <p style={{ fontSize: '14px' }}>{message}</p>
  </div>
);

// =============================================================================
// BOOKING PANELS
// =============================================================================

// Panel Wrapper
const Panel = ({ children, onClose }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '480px',
    background: theme.colors.white,
    boxShadow: theme.shadows.xl,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif"
  }}>
    {children}
  </div>
);

// Panel Header
const PanelHeader = ({ title, subtitle, dealId, onClose, variant = 'default' }) => {
  const bgColor = variant === 'booked' ? theme.colors.green : theme.colors.black;
  
  return (
    <div style={{
      padding: '24px',
      background: bgColor,
      color: theme.colors.white
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start' 
      }}>
        <div>
          {subtitle && (
            <p style={{ 
              fontSize: '12px', 
              opacity: 0.7, 
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: 500
            }}>
              {subtitle}
            </p>
          )}
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: 700,
            marginBottom: dealId ? '12px' : 0
          }}>
            {title}
          </h2>
          {dealId && (
            <span style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 12px',
              borderRadius: theme.radius.sm,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '13px',
              fontWeight: 500
            }}>
              {dealId}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: theme.colors.white,
            fontSize: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s ease'
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Section
const Section = ({ title, children, style = {} }) => (
  <div style={{ marginBottom: '24px', ...style }}>
    {title && (
      <h3 style={{
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: theme.colors.grey500,
        marginBottom: '12px'
      }}>
        {title}
      </h3>
    )}
    {children}
  </div>
);

// Input Field
const Input = ({ label, type = 'text', value, onChange, placeholder, required, mono = false, rows }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '13px',
      fontWeight: 600,
      color: theme.colors.black,
      marginBottom: '8px'
    }}>
      {label} {required && <span style={{ color: theme.colors.red }}>*</span>}
    </label>
    {rows ? (
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          padding: '12px 14px',
          border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md,
          fontSize: '14px',
          fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
          background: theme.colors.offWhite,
          resize: 'none',
          outline: 'none',
          transition: 'border-color 0.15s ease'
        }}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 14px',
          border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md,
          fontSize: '14px',
          fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
          background: theme.colors.offWhite,
          outline: 'none',
          transition: 'border-color 0.15s ease'
        }}
      />
    )}
  </div>
);

// Button
const Button = ({ children, onClick, disabled, variant = 'primary', fullWidth = false, size = 'md' }) => {
  const variants = {
    primary: { 
      bg: theme.colors.green, 
      color: theme.colors.white,
      hoverBg: '#2da362'
    },
    secondary: { 
      bg: theme.colors.black, 
      color: theme.colors.white,
      hoverBg: '#333'
    },
    outline: {
      bg: 'transparent',
      color: theme.colors.black,
      border: `1px solid ${theme.colors.grey300}`
    }
  };
  
  const sizes = {
    sm: { padding: '8px 16px', fontSize: '13px' },
    md: { padding: '14px 24px', fontSize: '15px' },
    lg: { padding: '18px 32px', fontSize: '16px' }
  };
  
  const style = variants[variant];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? theme.colors.grey300 : style.bg,
        color: disabled ? theme.colors.grey500 : style.color,
        border: style.border || 'none',
        borderRadius: theme.radius.md,
        ...sizes[size],
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: fullWidth ? '100%' : 'auto',
        transition: 'all 0.15s ease'
      }}
    >
      {children}
    </button>
  );
};

// =============================================================================
// BOOKED DETAIL PANEL
// =============================================================================

const BookedDetailPanel = ({ shipment, onClose }) => {
  const { carrier } = assignCarrier(shipment);
  const isDropOff = shipment.sendType === 'dropoff';
  
  return (
    <Panel>
      <PanelHeader
        title={shipment.carrier || carrier.name}
        subtitle="Booked via"
        dealId={shipment.dealId}
        onClose={onClose}
        variant="booked"
      />
      
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {/* Tracking */}
        <Section>
          <div style={{
            background: theme.colors.cream,
            borderRadius: theme.radius.lg,
            padding: '20px',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '11px', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: theme.colors.green,
              marginBottom: '8px'
            }}>
              Tracking Number
            </p>
            <p style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '18px',
              fontWeight: 600,
              color: theme.colors.black,
              marginBottom: '12px',
              wordBreak: 'break-all'
            }}>
              {shipment.trackingNumber || 'N/A'}
            </p>
            {shipment.trackingNumber && (
              <CopyButton text={shipment.trackingNumber} size="md" />
            )}
          </div>
        </Section>
        
        {/* Collection Address */}
        <Section title="Collection Address">
          <div style={{
            background: theme.colors.black,
            borderRadius: theme.radius.lg,
            padding: '20px',
            color: theme.colors.white
          }}>
            <p style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
              {shipment.customerName}
            </p>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '12px' }}>
              {shipment.collection_address}
            </p>
            {shipment.customerPhone && (
              <p style={{ fontSize: '13px', opacity: 0.7 }}>
                📞 {shipment.customerPhone}
              </p>
            )}
          </div>
        </Section>
        
        {/* Details */}
        <Section title="Shipment Details">
          <div style={{
            background: theme.colors.offWhite,
            borderRadius: theme.radius.lg,
            padding: '4px 16px'
          }}>
            <InfoRow label="Collection Date" value={formatDateLong(shipment.pickupDate || shipment.collection_date)} />
            <InfoRow 
              label="Package" 
              value={`${shipment.boxes?.length || 1} box${shipment.boxes?.length !== 1 ? 'es' : ''} · ${shipment.totalWeight}kg`} 
            />
            {shipment.price && (
              <InfoRow label="Cost" value={`£${parseFloat(shipment.price).toFixed(2)}`} />
            )}
          </div>
        </Section>
        
        {/* Customer */}
        <Section title="Customer">
          <div style={{
            background: theme.colors.offWhite,
            borderRadius: theme.radius.lg,
            padding: '4px 16px'
          }}>
            <InfoRow label="Name" value={shipment.customerName} copyable />
            <InfoRow label="Email" value={shipment.customerEmail} copyable />
            <InfoRow label="Phone" value={shipment.customerPhone} copyable />
          </div>
        </Section>
      </div>
      
      {/* Footer */}
      <div style={{ 
        padding: '20px 24px', 
        borderTop: `1px solid ${theme.colors.grey200}` 
      }}>
        <Button onClick={onClose} variant="secondary" fullWidth>
          Close
        </Button>
      </div>
    </Panel>
  );
};

// =============================================================================
// MANUAL BOOKING PANEL
// =============================================================================

const ManualBookingPanel = ({ shipment, carrier, onClose, onBook }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [pickupDate, setPickupDate] = useState(shipment.preferredPickupDate || '');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Label upload state
  const [labelFiles, setLabelFiles] = useState([]);
  const [uploadedLabelUrls, setUploadedLabelUrls] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const isDropOff = shipment.sendType === 'dropoff';
  const boxCount = shipment.boxes?.length || 1;
  const isNI = isNorthernIreland(shipment.postcode);
  
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  
  const wasAutoFilled = shipment.preferredPickupDate && pickupDate === shipment.preferredPickupDate;
  
  // Validation
  const hasRequiredLabels = isDropOff ? uploadedLabelUrls.length >= boxCount : true;
  const hasRequiredDate = isDropOff ? true : !!pickupDate;
  const canBook = trackingNumber && shippingCost && hasRequiredLabels && hasRequiredDate;

  // File handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setLabelFiles(prev => [...prev, ...files]);
    setUploadError(null);
  };

  const handleUploadLabels = async () => {
    if (labelFiles.length === 0) return;
    
    setUploadError(null);
    setIsUploading(true);
    
    try {
      if (labelFiles.length === 1) {
        const result = await api.uploadLabel(labelFiles[0], shipment.dealId);
        if (result.success) {
          setUploadedLabelUrls(prev => [...prev, result.url]);
          setLabelFiles([]);
        } else {
          setUploadError(result.error || 'Upload failed');
        }
      } else {
        const result = await api.uploadMultipleLabels(labelFiles, shipment.dealId);
        if (result.success || result.urls?.length > 0) {
          setUploadedLabelUrls(prev => [...prev, ...result.urls.map(u => u.url)]);
          setLabelFiles([]);
        } else {
          setUploadError(result.error || 'Upload failed');
        }
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedLabel = (index) => {
    setUploadedLabelUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removePendingFile = (index) => {
    setLabelFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBook = async () => {
    if (!canBook) return;
    setIsProcessing(true);
    
    try {
      await api.updateDealStage(shipment.dealId, 'presentationscheduled', {
        trackingNumber,
        carrier: carrier.name,
        pickupDate: isDropOff ? null : pickupDate,
        price: parseFloat(shippingCost),
        labelLink: uploadedLabelUrls[0] || null
      });

      if (shipment.customerEmail) {
        await api.sendEmail({
          customerEmail: shipment.customerEmail,
          customerName: shipment.customerName,
          collectionAddress: shipment.collection_address,
          pickupDate: isDropOff ? null : pickupDate,
          pickupTimeWindow: '9am - 5pm',
          boxCount: boxCount,
          trackingNumber,
          carrier: carrier.name,
          sendType: shipment.sendType,
          hasPrinter: shipment.hasPrinter,
          qrCodeUrl: uploadedLabelUrls[0] || null,
          labelUrls: uploadedLabelUrls
        });
      }
    } catch (err) {
      console.error('Booking error:', err);
    }
    
    onBook({
      ...shipment,
      status: 'shipping_arranged',
      trackingNumber,
      carrier: carrier.id,
      pickupDate: isDropOff ? null : pickupDate,
      price: parseFloat(shippingCost),
      labelUrls: uploadedLabelUrls
    });
    
    setIsProcessing(false);
  };

  return (
    <Panel>
      <PanelHeader
        title={carrier.name}
        subtitle="Book Shipment"
        dealId={shipment.dealId}
        onClose={onClose}
      />
      
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {/* Send Type Badge */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Badge variant={isDropOff ? 'info' : 'success'} size="md">
            {isDropOff ? '📍 Customer Drop-off' : '🚚 Courier Collection'}
          </Badge>
          {isNI && <Badge variant="warning" size="md">Northern Ireland</Badge>}
          {shipment.hasPrinter && <Badge variant="default" size="md">🖨️ Has Printer</Badge>}
        </div>

        {/* Deal Name / Product */}
        {shipment.dealName && (
          <Section title="Item">
            <div style={{
              background: theme.colors.offWhite,
              borderRadius: theme.radius.lg,
              padding: '16px',
              fontSize: '14px',
              color: theme.colors.black,
              lineHeight: 1.5
            }}>
              {shipment.dealName}
            </div>
          </Section>
        )}

        {/* Quick Copy Fields */}
        <Section title="Quick Copy">
          <div style={{
            background: theme.colors.offWhite,
            borderRadius: theme.radius.lg,
            padding: '4px 16px'
          }}>
            <InfoRow label="Reference" value={shipment.dealId} mono copyable />
            <InfoRow label="Customer" value={shipment.customerName} copyable />
            <InfoRow label="Phone" value={shipment.customerPhone} mono copyable />
            <InfoRow label="Postcode" value={shipment.postcode} mono copyable />
            <InfoRow label="Email" value={shipment.customerEmail} copyable />
          </div>
        </Section>
        
        {/* Collection Address */}
        <Section title="Collection Address">
          <div style={{
            background: theme.colors.black,
            borderRadius: theme.radius.lg,
            padding: '20px',
            color: theme.colors.white
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}>
              <p style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>
                {shipment.customerName}
              </p>
              <CopyButton 
                text={`${shipment.customerName}\n${shipment.collection_address || shipment.shippingAddress?.full || ''}`} 
                size="sm" 
              />
            </div>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '12px', margin: 0 }}>
              {shipment.collection_address || shipment.shippingAddress?.full}
            </p>
            {shipment.customerPhone && (
              <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>
                📞 {shipment.customerPhone}
              </p>
            )}
          </div>
        </Section>
        
        {/* Package Details with Dimensions */}
        <Section title="Package Details">
          <div style={{
            background: theme.colors.offWhite,
            borderRadius: theme.radius.lg,
            padding: '16px'
          }}>
            {/* Summary Stats */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px',
                background: theme.colors.white,
                borderRadius: theme.radius.md
              }}>
                <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.black, margin: 0 }}>
                  {boxCount}
                </p>
                <p style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 500, margin: 0 }}>
                  Box{boxCount !== 1 ? 'es' : ''}
                </p>
              </div>
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px',
                background: theme.colors.white,
                borderRadius: theme.radius.md
              }}>
                <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.black, margin: 0 }}>
                  {shipment.totalWeight}
                </p>
                <p style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 500, margin: 0 }}>
                  kg Total
                </p>
              </div>
            </div>
            
            {/* Box Dimensions */}
            {shipment.package_details && (
              <div style={{
                background: theme.colors.white,
                borderRadius: theme.radius.md,
                padding: '12px 14px',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '12px',
                color: theme.colors.grey600,
                whiteSpace: 'pre-line',
                lineHeight: 1.8
              }}>
                {shipment.package_details.replace(/\s*(Box\s*\d+)/gi, '\n$1').trim()}
              </div>
            )}
            
            {/* Multi-box warning */}
            {boxCount > 1 && isDropOff && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: theme.colors.yellowLight,
                border: `1px solid ${theme.colors.yellow}`,
                borderRadius: theme.radius.md,
                fontSize: '12px',
                color: '#8B7500'
              }}>
                <strong>⚠️ {boxCount} boxes = {boxCount} separate labels required</strong>
                <br />
                <span style={{ fontSize: '11px' }}>Each box needs its own label for drop-off</span>
              </div>
            )}
          </div>
        </Section>

        {/* Label Upload - Only for Drop-offs */}
        {isDropOff && (
          <Section title={`Shipping Label${boxCount > 1 ? 's' : ''}`}>
            {boxCount > 1 && (
              <p style={{ 
                fontSize: '13px', 
                color: theme.colors.grey500, 
                marginBottom: '12px' 
              }}>
                Upload {boxCount} labels (one per box)
              </p>
            )}
            
            {/* Uploaded labels */}
            {uploadedLabelUrls.map((url, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: theme.colors.greenLight,
                border: `1px solid ${theme.colors.green}`,
                borderRadius: theme.radius.md,
                marginBottom: '8px'
              }}>
                <span style={{ color: theme.colors.green, fontSize: '16px' }}>✓</span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                  Label {idx + 1}
                </span>
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.colors.blue, 
                    fontSize: '12px',
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  View
                </a>
                <button 
                  onClick={() => removeUploadedLabel(idx)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: theme.colors.red, 
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            {/* Pending files */}
            {labelFiles.map((file, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: theme.colors.yellowLight,
                border: `1px solid ${theme.colors.yellow}`,
                borderRadius: theme.radius.md,
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>📄</span>
                <span style={{ 
                  flex: 1, 
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {file.name}
                </span>
                <span style={{ fontSize: '11px', color: theme.colors.grey500 }}>
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <button 
                  onClick={() => removePendingFile(idx)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: theme.colors.red, 
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            {/* Upload button for pending files */}
            {labelFiles.length > 0 && (
              <Button 
                onClick={handleUploadLabels} 
                disabled={isUploading}
                fullWidth
                size="sm"
              >
                {isUploading ? 'Uploading...' : `Upload ${labelFiles.length} file${labelFiles.length > 1 ? 's' : ''}`}
              </Button>
            )}

            {/* File input */}
            {uploadedLabelUrls.length < boxCount && labelFiles.length === 0 && (
              <div style={{
                border: `2px dashed ${theme.colors.grey300}`,
                borderRadius: theme.radius.lg,
                padding: '32px',
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                background: theme.colors.offWhite
              }}>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple={boxCount > 1}
                  onChange={handleFileSelect}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme.colors.black,
                  marginBottom: '4px'
                }}>
                  Click to upload label{boxCount > 1 ? 's' : ''}
                </p>
                <p style={{ fontSize: '12px', color: theme.colors.grey500 }}>
                  PNG, JPG or PDF
                </p>
              </div>
            )}

            {uploadError && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: theme.colors.redLight,
                borderRadius: theme.radius.md,
                fontSize: '13px',
                color: theme.colors.red
              }}>
                {uploadError}
              </div>
            )}
            
            {/* Status */}
            <p style={{ 
              fontSize: '12px', 
              color: theme.colors.grey500, 
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {uploadedLabelUrls.length} of {boxCount} uploaded
              {uploadedLabelUrls.length >= boxCount && (
                <span style={{ color: theme.colors.green }}>✓</span>
              )}
            </p>
          </Section>
        )}

        {/* Collection Date - Only for Collections */}
        {!isDropOff && (
          <Section title="Collection Date">
            <Input
              type="date"
              value={pickupDate}
              onChange={(e) => setPickupDate(e.target.value)}
              required
            />
            {wasAutoFilled && <Badge variant="success" size="sm">From HubSpot</Badge>}
          </Section>
        )}

        {/* Booking Details */}
        <Section title="Booking Details">
          {/* Only Royal Mail drop-off needs multiple tracking numbers (one per box/label) */}
          {boxCount > 1 && isDropOff && carrier.id === 'royal_mail' ? (
            <Input
              label="Tracking Numbers"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
              placeholder={`Enter ${boxCount} tracking numbers, one per line`}
              required
              mono
              rows={boxCount + 1}
            />
          ) : (
            <Input
              label="Tracking Number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
              placeholder="Enter tracking number"
              required
              mono
            />
          )}
          
          <Input
            label="Shipping Cost"
            type="number"
            value={shippingCost}
            onChange={(e) => setShippingCost(e.target.value)}
            placeholder="0.00"
            required
          />
        </Section>
      </div>
      
      {/* Footer */}
      <div style={{ 
        padding: '20px 24px', 
        borderTop: `1px solid ${theme.colors.grey200}`,
        background: theme.colors.offWhite
      }}>
        <Button 
          onClick={handleBook} 
          disabled={!canBook || isProcessing}
          fullWidth
          size="lg"
        >
          {isProcessing ? 'Processing...' : `Confirm Booking · £${shippingCost || '0.00'}`}
        </Button>
      </div>
    </Panel>
  );
};

// =============================================================================
// FEDEX PANEL
// =============================================================================

const FedExDetailPanel = ({ shipment, onClose, onBook }) => {
  const [step, setStep] = useState('loading');
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualCost, setManualCost] = useState('');
  const [manualTracking, setManualTracking] = useState('');
  
  // Label upload state for manual mode
  const [labelFiles, setLabelFiles] = useState([]);
  const [uploadedLabelUrls, setUploadedLabelUrls] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const boxCount = shipment.boxes?.length || 1;
  
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minPickupDate = tomorrow.toISOString().split('T')[0];
  
  const maxDirect = new Date();
  maxDirect.setDate(maxDirect.getDate() + 14);

  const getInitialPickupDate = () => {
    if (shipment.preferredPickupDate) {
      const preferred = new Date(shipment.preferredPickupDate);
      const minDate = new Date(minPickupDate);
      return preferred >= minDate ? shipment.preferredPickupDate : minPickupDate;
    }
    return '';
  };
  
  const [pickupDate, setPickupDate] = useState(getInitialPickupDate());
  const [pickupTime, setPickupTime] = useState('09:00');
  
  const wasAutoFilled = shipment.preferredPickupDate && pickupDate === shipment.preferredPickupDate;

  // File handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setLabelFiles(prev => [...prev, ...files]);
    setUploadError(null);
  };

  const handleUploadLabels = async () => {
    if (labelFiles.length === 0) return;
    setUploadError(null);
    setIsUploading(true);
    
    try {
      if (labelFiles.length === 1) {
        const result = await api.uploadLabel(labelFiles[0], shipment.dealId);
        if (result.success) {
          setUploadedLabelUrls(prev => [...prev, result.url]);
          setLabelFiles([]);
        } else {
          setUploadError(result.error || 'Upload failed');
        }
      } else {
        const result = await api.uploadMultipleLabels(labelFiles, shipment.dealId);
        if (result.success || result.urls?.length > 0) {
          setUploadedLabelUrls(prev => [...prev, ...result.urls.map(u => u.url)]);
          setLabelFiles([]);
        } else {
          setUploadError(result.error || 'Upload failed');
        }
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedLabel = (index) => {
    setUploadedLabelUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removePendingFile = (index) => {
    setLabelFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    setStep('loading');
    await new Promise(r => setTimeout(r, 1200));
    const { rates: filteredRates, cheapest } = filterAndSelectCheapest(mockRates);
    setRates(filteredRates);
    setSelectedRate(cheapest);
    setStep('rates');
  };

  const handleApproveAndBook = async () => {
    if (manualMode) {
      if (!manualTracking || !manualCost || !pickupDate || uploadedLabelUrls.length === 0) return;
    } else {
      if (!selectedRate || !pickupDate) return;
    }
    
    setIsProcessing(true);
    setStep('booking');
    
    await new Promise(r => setTimeout(r, 1500));
    
    const trackingNumber = manualMode ? manualTracking : `794644790${Math.floor(Math.random() * 1000)}`;
    const price = manualMode ? parseFloat(manualCost) : selectedRate.price.totalNet;
    
    const bookingResult = {
      success: true,
      trackingNumber,
      pickupConfirmation: `PCK${Date.now()}`,
      pickupStatus: new Date(pickupDate) <= maxDirect ? 'scheduled' : 'queued'
    };
    
    try {
      await api.updateDealStage(shipment.dealId, 'presentationscheduled', {
        trackingNumber,
        carrier: 'FedEx',
        pickupDate,
        price,
        labelLink: uploadedLabelUrls[0] || null
      });

      if (shipment.customerEmail) {
        await api.sendEmail({
          customerEmail: shipment.customerEmail,
          customerName: shipment.customerName,
          collectionAddress: shipment.collection_address,
          pickupDate,
          pickupTimeWindow: `${pickupTime} - 17:00`,
          boxCount: boxCount,
          trackingNumber,
          carrier: 'FedEx',
          sendType: 'collection',
          hasPrinter: shipment.hasPrinter,
          labelUrls: uploadedLabelUrls
        });
      }
    } catch (err) {
      console.error('Failed to update HubSpot:', err);
    }
    
    setResult(bookingResult);
    setStep('complete');
    
    onBook({
      ...shipment,
      status: 'shipping_arranged',
      trackingNumber,
      carrier: 'fedex',
      serviceType: manualMode ? 'MANUAL' : selectedRate?.serviceType,
      pickupDate,
      price,
      labelUrls: uploadedLabelUrls
    });
    
    setIsProcessing(false);
  };

  const cheapestRate = rates[0];
  
  // Manual mode requires: tracking, cost, date, AND label file (FedEx provides single PDF with all labels)
  const hasRequiredLabels = uploadedLabelUrls.length >= 1;
  const canBook = manualMode 
    ? (manualTracking && manualCost && pickupDate && hasRequiredLabels)
    : (selectedRate && pickupDate);

  return (
    <Panel>
      <PanelHeader
        title="FedEx"
        subtitle={step === 'complete' ? 'Booked' : 'Book Shipment'}
        dealId={shipment.dealId}
        onClose={onClose}
        variant={step === 'complete' ? 'booked' : 'default'}
      />
      
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {/* Loading */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: `3px solid ${theme.colors.grey200}`,
              borderTopColor: theme.colors.black,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ color: theme.colors.grey500, fontWeight: 500 }}>
              Fetching rates...
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {/* Rates */}
        {step === 'rates' && (
          <>
            {/* Badges */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Badge variant="success" size="md">🚚 Courier Collection</Badge>
              {isNorthernIreland(shipment.postcode) && <Badge variant="warning" size="md">Northern Ireland</Badge>}
              {shipment.hasPrinter && <Badge variant="default" size="md">🖨️ Has Printer</Badge>}
            </div>

            {/* Deal Name / Product */}
            {shipment.dealName && (
              <Section title="Item">
                <div style={{
                  background: theme.colors.offWhite,
                  borderRadius: theme.radius.lg,
                  padding: '16px',
                  fontSize: '14px',
                  color: theme.colors.black,
                  lineHeight: 1.5
                }}>
                  {shipment.dealName}
                </div>
              </Section>
            )}

            {/* Quick Copy Fields */}
            <Section title="Quick Copy">
              <div style={{
                background: theme.colors.offWhite,
                borderRadius: theme.radius.lg,
                padding: '4px 16px'
              }}>
                <InfoRow label="Reference" value={shipment.dealId} mono copyable />
                <InfoRow label="Customer" value={shipment.customerName} copyable />
                <InfoRow label="Phone" value={shipment.customerPhone} mono copyable />
                <InfoRow label="Postcode" value={shipment.postcode} mono copyable />
                <InfoRow label="Email" value={shipment.customerEmail} copyable />
              </div>
            </Section>

            {/* Collection Address */}
            <Section title="Collection Address">
              <div style={{
                background: theme.colors.black,
                borderRadius: theme.radius.lg,
                padding: '20px',
                color: theme.colors.white
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <p style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>
                    {shipment.customerName}
                  </p>
                  <CopyButton 
                    text={`${shipment.customerName}\n${shipment.collection_address || ''}`} 
                    size="sm" 
                  />
                </div>
                <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '12px', margin: 0 }}>
                  {shipment.collection_address || shipment.shippingAddress?.full}
                </p>
                {shipment.customerPhone && (
                  <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>
                    📞 {shipment.customerPhone}
                  </p>
                )}
              </div>
            </Section>

            {/* Package Details with Dimensions */}
            <Section title="Package Details">
              <div style={{
                background: theme.colors.offWhite,
                borderRadius: theme.radius.lg,
                padding: '16px'
              }}>
                {/* Summary Stats */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '12px',
                    background: theme.colors.white,
                    borderRadius: theme.radius.md
                  }}>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.black, margin: 0 }}>
                      {boxCount}
                    </p>
                    <p style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 500, margin: 0 }}>
                      Box{boxCount !== 1 ? 'es' : ''}
                    </p>
                  </div>
                  <div style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '12px',
                    background: theme.colors.white,
                    borderRadius: theme.radius.md
                  }}>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.black, margin: 0 }}>
                      {shipment.totalWeight}
                    </p>
                    <p style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 500, margin: 0 }}>
                      kg Total
                    </p>
                  </div>
                </div>
                
                {/* Box Dimensions */}
                {shipment.package_details && (
                  <div style={{
                    background: theme.colors.white,
                    borderRadius: theme.radius.md,
                    padding: '12px 14px',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    color: theme.colors.grey600,
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8
                  }}>
                    {shipment.package_details.replace(/\s*(Box\s*\d+)/gi, '\n$1').trim()}
                  </div>
                )}
              </div>
            </Section>

            {/* Sandbox Notice */}
            <div style={{
              background: theme.colors.yellowLight,
              border: `1px solid ${theme.colors.yellow}`,
              borderRadius: theme.radius.md,
              padding: '14px 16px',
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '13px', color: '#8B7500' }}>
                ⚠️ Sandbox mode - test prices only
              </span>
              <button
                onClick={() => setManualMode(!manualMode)}
                style={{
                  background: theme.colors.black,
                  color: theme.colors.white,
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {manualMode ? 'Show Rates' : 'Manual Entry'}
              </button>
            </div>

            {manualMode ? (
              <>
                {/* Manual Mode */}
                <Section title="Upload FedEx Label">
                  {boxCount > 1 && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: theme.colors.grey500, 
                      marginBottom: '12px' 
                    }}>
                      Upload the FedEx PDF (contains all {boxCount} box labels)
                    </p>
                  )}
                  
                  {/* Uploaded labels */}
                  {uploadedLabelUrls.map((url, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: theme.colors.greenLight,
                      border: `1px solid ${theme.colors.green}`,
                      borderRadius: theme.radius.md,
                      marginBottom: '8px'
                    }}>
                      <span style={{ color: theme.colors.green }}>✓</span>
                      <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                        FedEx Label{boxCount > 1 ? ` (${boxCount} boxes)` : ''}
                      </span>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{ color: theme.colors.blue, fontSize: '12px', textDecoration: 'none' }}>
                        View
                      </a>
                      <button onClick={() => removeUploadedLabel(idx)}
                        style={{ background: 'none', border: 'none', color: theme.colors.red, cursor: 'pointer', fontSize: '18px' }}>
                        ×
                      </button>
                    </div>
                  ))}

                  {/* Pending files */}
                  {labelFiles.map((file, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: theme.colors.yellowLight,
                      borderRadius: theme.radius.md,
                      marginBottom: '8px'
                    }}>
                      <span>📄</span>
                      <span style={{ flex: 1, fontSize: '13px' }}>{file.name}</span>
                      <button onClick={() => removePendingFile(idx)}
                        style={{ background: 'none', border: 'none', color: theme.colors.red, cursor: 'pointer' }}>
                        ×
                      </button>
                    </div>
                  ))}

                  {labelFiles.length > 0 && (
                    <Button onClick={handleUploadLabels} disabled={isUploading} fullWidth size="sm">
                      {isUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  )}

                  {uploadedLabelUrls.length === 0 && labelFiles.length === 0 && (
                    <div style={{
                      border: `2px dashed ${theme.colors.grey300}`,
                      borderRadius: theme.radius.lg,
                      padding: '32px',
                      textAlign: 'center',
                      position: 'relative',
                      background: theme.colors.offWhite
                    }}>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                      />
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
                      <p style={{ fontSize: '14px', fontWeight: 600 }}>Upload FedEx label PDF</p>
                      <p style={{ fontSize: '12px', color: theme.colors.grey500 }}>Single PDF with all box labels</p>
                    </div>
                  )}

                  {uploadError && (
                    <div style={{ marginTop: '12px', padding: '12px', background: theme.colors.redLight, borderRadius: theme.radius.md, color: theme.colors.red, fontSize: '13px' }}>
                      {uploadError}
                    </div>
                  )}
                </Section>

                <Section title="Booking Details">
                  <Input
                    label="Tracking Number"
                    value={manualTracking}
                    onChange={(e) => setManualTracking(e.target.value.toUpperCase())}
                    placeholder="Enter FedEx tracking"
                    required
                    mono
                  />
                  <Input
                    label="Shipping Cost"
                    type="number"
                    value={manualCost}
                    onChange={(e) => setManualCost(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                  <Input
                    label="Collection Date"
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    required
                  />
                  {wasAutoFilled && <Badge variant="success" size="sm">From HubSpot</Badge>}
                </Section>
              </>
            ) : (
              <>
                {/* Rate Selection */}
                <Section title="Select Service">
                  {rates.map(rate => {
                    const isCheapest = rate.serviceType === cheapestRate?.serviceType;
                    const isSelected = selectedRate?.serviceType === rate.serviceType;
                    
                    return (
                      <div
                        key={rate.serviceType}
                        onClick={() => setSelectedRate(rate)}
                        style={{
                          padding: '16px',
                          border: `2px solid ${isSelected ? theme.colors.green : theme.colors.grey200}`,
                          borderRadius: theme.radius.lg,
                          cursor: 'pointer',
                          background: isSelected ? theme.colors.greenLight : theme.colors.white,
                          marginBottom: '12px',
                          position: 'relative'
                        }}
                      >
                        {isCheapest && (
                          <Badge variant="success" size="sm" style={{
                            position: 'absolute',
                            top: '-10px',
                            right: '12px'
                          }}>
                            Cheapest
                          </Badge>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <p style={{ fontWeight: 600, color: theme.colors.black, marginBottom: '4px' }}>
                              {rate.serviceName}
                            </p>
                            <p style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                              {rate.transitDays} day{rate.transitDays > 1 ? 's' : ''}
                            </p>
                          </div>
                          <p style={{ fontSize: '20px', fontWeight: 700, color: theme.colors.black }}>
                            £{rate.price.totalNet.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </Section>

                {selectedRate && (
                  <Section title="Collection Date">
                    <Input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      required
                    />
                    {wasAutoFilled && <Badge variant="success" size="sm">From HubSpot</Badge>}
                  </Section>
                )}
              </>
            )}
          </>
        )}

        {/* Booking */}
        {step === 'booking' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: `3px solid ${theme.colors.grey200}`,
              borderTopColor: theme.colors.green,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ fontWeight: 600, color: theme.colors.black, marginBottom: '8px' }}>
              Creating shipment...
            </p>
            <p style={{ fontSize: '13px', color: theme.colors.grey500 }}>
              Generating label & scheduling pickup
            </p>
          </div>
        )}

        {/* Complete */}
        {step === 'complete' && result && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: theme.colors.greenLight,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '36px'
            }}>
              ✓
            </div>
            <h3 style={{ marginBottom: '8px', color: theme.colors.black }}>Shipment Booked!</h3>
            <p style={{ color: theme.colors.grey500, marginBottom: '24px' }}>
              Pickup {result.pickupStatus}
            </p>
            
            <div style={{
              background: theme.colors.offWhite,
              borderRadius: theme.radius.lg,
              padding: '20px',
              textAlign: 'left'
            }}>
              <InfoRow label="Tracking" value={result.trackingNumber} mono copyable />
              <InfoRow label="Pickup Date" value={formatDateLong(pickupDate)} />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {step === 'rates' && (
        <div style={{ 
          padding: '20px 24px', 
          borderTop: `1px solid ${theme.colors.grey200}`,
          background: theme.colors.offWhite
        }}>
          <Button
            onClick={handleApproveAndBook}
            disabled={!canBook || isProcessing}
            fullWidth
            size="lg"
          >
            {isProcessing ? 'Processing...' : manualMode 
              ? `Confirm · £${manualCost || '0.00'}`
              : `Approve · £${selectedRate?.price.totalNet.toFixed(2) || '0.00'}`}
          </Button>
        </div>
      )}

      {step === 'complete' && (
        <div style={{ padding: '20px 24px', borderTop: `1px solid ${theme.colors.grey200}` }}>
          <Button onClick={onClose} variant="secondary" fullWidth>
            Done
          </Button>
        </div>
      )}
    </Panel>
  );
};

// =============================================================================
// MAIN APP
// =============================================================================

export default function AudioTraderShipping() {
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchDeals() {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getDeals();
        setShipments(data.deals || []);
      } catch (err) {
        console.error('Failed to fetch deals:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchDeals();
  }, []);

  const shipmentsByCarrier = useMemo(() => {
    const grouped = { royal_mail: [], parcelforce: [], fedex: [], shiply: [] };
    shipments.forEach(shipment => {
      if (shipment.status !== 'ready_to_ship') return;
      
      // If carrier is already set in HubSpot, respect it (manual override)
      let carrierId;
      if (shipment.carrier) {
        // Normalize the carrier name from HubSpot
        const carrierLower = shipment.carrier.toLowerCase();
        if (carrierLower.includes('royal')) carrierId = 'royal_mail';
        else if (carrierLower.includes('parcel')) carrierId = 'parcelforce';
        else if (carrierLower.includes('fedex') || carrierLower.includes('fed')) carrierId = 'fedex';
        else if (carrierLower.includes('shiply')) carrierId = 'shiply';
        else carrierId = assignCarrier(shipment).carrierId; // Fallback to calculation
      } else {
        // No carrier set, calculate based on shipment properties
        carrierId = assignCarrier(shipment).carrierId;
      }
      
      grouped[carrierId]?.push(shipment);
    });
    return grouped;
  }, [shipments]);

  const bookedShipments = useMemo(() => 
    shipments.filter(s => s.status === 'shipping_arranged'),
  [shipments]);

  const handleBook = (updated) => {
    setShipments(prev => prev.map(s => s.id === updated.id ? updated : s));
    setSelectedShipment(null);
  };

  const totalReady = shipments.filter(s => s.status === 'ready_to_ship').length;
  const totalBooked = bookedShipments.length;

  // Get carrier for selected shipment - respect HubSpot value if set
  const getCarrierForShipment = (shipment) => {
    if (!shipment) return null;
    
    if (shipment.carrier) {
      const carrierLower = shipment.carrier.toLowerCase();
      if (carrierLower.includes('royal')) return CARRIERS.royal_mail;
      if (carrierLower.includes('parcel')) return CARRIERS.parcelforce;
      if (carrierLower.includes('fedex') || carrierLower.includes('fed')) return CARRIERS.fedex;
      if (carrierLower.includes('shiply')) return CARRIERS.shiply;
    }
    return assignCarrier(shipment).carrier;
  };

  const selectedCarrier = getCarrierForShipment(selectedShipment);
  const isFedEx = selectedCarrier?.id === 'fedex';

  // Loading
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: theme.colors.cream, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: "'DM Sans', sans-serif"
      }}>
        <style>{globalStyles}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: `3px solid ${theme.colors.grey200}`,
            borderTopColor: theme.colors.black,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: theme.colors.grey500, fontWeight: 500 }}>Loading shipments...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: theme.colors.cream, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: "'DM Sans', sans-serif"
      }}>
        <style>{globalStyles}</style>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            background: theme.colors.redLight,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '28px'
          }}>
            ⚠
          </div>
          <h2 style={{ marginBottom: '8px', color: theme.colors.black }}>Failed to load</h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '24px', fontSize: '14px' }}>{error}</p>
          <Button onClick={() => window.location.reload()} variant="secondary">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: theme.colors.cream,
      fontFamily: "'DM Sans', sans-serif"
    }}>
      <style>{globalStyles}</style>

      {/* Header */}
      <header style={{
        background: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.grey200}`,
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img 
            src="https://pub-568acfa90a484bd2a62f611e9d8985b5.r2.dev/AT_logo.png" 
            alt="Audio Trader" 
            style={{ height: '40px' }}
          />
          <div style={{ 
            width: '1px', 
            height: '24px', 
            background: theme.colors.grey200 
          }} />
          <h1 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: theme.colors.black 
          }}>
            Shipping
          </h1>
        </div>
        
        <div style={{ display: 'flex', gap: '32px' }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 700, 
              color: theme.colors.black,
              lineHeight: 1
            }}>
              {totalReady}
            </p>
            <p style={{ 
              fontSize: '11px', 
              color: theme.colors.grey500,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              To Book
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 700, 
              color: theme.colors.green,
              lineHeight: 1
            }}>
              {totalBooked}
            </p>
            <p style={{ 
              fontSize: '11px', 
              color: theme.colors.grey500,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Booked
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ 
        padding: '24px 32px', 
        display: 'flex', 
        gap: '20px', 
        overflowX: 'auto',
        minHeight: 'calc(100vh - 80px)'
      }}>
        {/* Carrier Columns */}
        <Column 
          title="Royal Mail" 
          count={shipmentsByCarrier.royal_mail.length}
          color={CARRIERS.royal_mail.color}
          icon="📮"
        >
          {shipmentsByCarrier.royal_mail.length === 0 ? (
            <EmptyState message="No shipments" />
          ) : (
            shipmentsByCarrier.royal_mail.map(s => (
              <ShipmentCard 
                key={s.id} 
                shipment={s} 
                onClick={() => setSelectedShipment(s)}
                isSelected={selectedShipment?.id === s.id}
              />
            ))
          )}
        </Column>

        <Column 
          title="ParcelForce" 
          count={shipmentsByCarrier.parcelforce.length}
          color={CARRIERS.parcelforce.color}
          icon="📦"
        >
          {shipmentsByCarrier.parcelforce.length === 0 ? (
            <EmptyState message="No shipments" />
          ) : (
            shipmentsByCarrier.parcelforce.map(s => (
              <ShipmentCard 
                key={s.id} 
                shipment={s} 
                onClick={() => setSelectedShipment(s)}
                isSelected={selectedShipment?.id === s.id}
              />
            ))
          )}
        </Column>

        <Column 
          title="FedEx" 
          count={shipmentsByCarrier.fedex.length}
          color={CARRIERS.fedex.color}
          icon="✈️"
        >
          {shipmentsByCarrier.fedex.length === 0 ? (
            <EmptyState message="No shipments" />
          ) : (
            shipmentsByCarrier.fedex.map(s => (
              <ShipmentCard 
                key={s.id} 
                shipment={s} 
                onClick={() => setSelectedShipment(s)}
                isSelected={selectedShipment?.id === s.id}
              />
            ))
          )}
        </Column>

        <Column 
          title="Shiply" 
          count={shipmentsByCarrier.shiply.length}
          color={CARRIERS.shiply.color}
          icon="🚛"
        >
          {shipmentsByCarrier.shiply.length === 0 ? (
            <EmptyState message="No shipments" />
          ) : (
            shipmentsByCarrier.shiply.map(s => (
              <ShipmentCard 
                key={s.id} 
                shipment={s} 
                onClick={() => setSelectedShipment(s)}
                isSelected={selectedShipment?.id === s.id}
              />
            ))
          )}
        </Column>

        {/* Booked Column */}
        <Column 
          title="Booked" 
          count={bookedShipments.length}
          color={theme.colors.green}
          icon="✓"
          variant="booked"
        >
          {bookedShipments.length === 0 ? (
            <EmptyState message="No booked shipments" />
          ) : (
            bookedShipments.map(s => (
              <BookedShipmentCard 
                key={s.id} 
                shipment={s} 
                onClick={() => setSelectedShipment(s)}
                isSelected={selectedShipment?.id === s.id}
              />
            ))
          )}
        </Column>
      </main>

      {/* Detail Panel */}
      {selectedShipment && (
        <>
          <div
            onClick={() => setSelectedShipment(null)}
            style={{ 
              position: 'fixed', 
              inset: 0, 
              background: 'rgba(0,0,0,0.4)', 
              zIndex: 999 
            }}
          />
          {selectedShipment.status === 'shipping_arranged' ? (
            <BookedDetailPanel
              shipment={selectedShipment}
              onClose={() => setSelectedShipment(null)}
            />
          ) : isFedEx ? (
            <FedExDetailPanel
              shipment={selectedShipment}
              onClose={() => setSelectedShipment(null)}
              onBook={handleBook}
            />
          ) : (
            <ManualBookingPanel
              shipment={selectedShipment}
              carrier={selectedCarrier}
              onClose={() => setSelectedShipment(null)}
              onBook={handleBook}
            />
          )}
        </>
      )}
    </div>
  );
}
